import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Page, PageHeader } from "../components/PageHeader";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { naira, compactNaira, fmtDateTime, titleCase } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Download, Trophy, Landmark, ScrollText } from "lucide-react";

const PALETTE = ["#8B1E1E", "#C49A4A", "#A65C2C", "#4F7A52", "#B7791F", "#6B1717", "#8a6f5c"];
const RANK = ["#C49A4A", "#B7A98F", "#A9825B"];

function exportCsv(filename, rows) {
  if (!rows || !rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
}

export default function Reports() {
  const [sales, setSales] = useState(null);
  const [league, setLeague] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pmStart, setPmStart] = useState("");
  const [pmEnd, setPmEnd] = useState("");
  const [pmTill, setPmTill] = useState("");

  const loadSales = () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/reports/sales", { params }).then(setSales).catch(() => {});
  };
  const loadLedger = () => {
    const params = {};
    if (pmStart) params.start = pmStart;
    if (pmEnd) params.end = pmEnd;
    if (pmTill) params.till = pmTill;
    api.get("/reports/payment-ledger", { params }).then(setLedger).catch(() => {});
  };
  useEffect(() => { loadSales(); loadLedger(); api.get("/reports/locations").then(setLeague).catch(() => {}); }, []);

  const orderCols = [
    { key: "number", label: "Order", render: (r) => <span className="font-semibold" style={{ color: "#8B1E1E" }}>{r.number}</span> },
    { key: "customer_name", label: "Customer" },
    { key: "order_type", label: "Type", render: (r) => titleCase(r.order_type) },
    { key: "channel", label: "Channel", render: (r) => <span className="text-xs uppercase px-2 py-0.5 rounded" style={{ background: "#F3ECE2", color: "#765A4A" }}>{r.channel}</span> },
    { key: "date", label: "Time", render: (r) => fmtDateTime(r.date) },
    { key: "total", label: "Total", align: "right", render: (r) => <span className="font-semibold">{naira(r.total)}</span> },
    { key: "fulfilment_status", label: "Status", render: (r) => <StatusBadge status={r.fulfilment_status} /> },
  ];

  return (
    <Page>
      <PageHeader title="Reports" subtitle="Sales analytics & outlet performance across all locations" />
      <Tabs defaultValue="sales">
        <TabsList className="mb-4">
          <TabsTrigger value="sales" data-testid="tab-rep-sales">Sales</TabsTrigger>
          <TabsTrigger value="outlets" data-testid="tab-rep-outlets">Outlets League</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-rep-ledger">Payment Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="sales">
          <div className="flex flex-wrap items-end gap-3 mb-6 rounded-xl border p-4" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}>
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="rep-start" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="rep-end" /></div>
            <Button onClick={loadSales} data-testid="rep-apply" className="h-9 text-white" style={{ background: "#8B1E1E" }}>Apply Filter</Button>
            {sales && <Button variant="outline" className="h-9" onClick={() => exportCsv("sales.csv", sales.orders)} data-testid="rep-export"><Download size={15} className="mr-1" /> Export CSV</Button>}
          </div>
          {sales && (<>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <Stat label="Total Sales" value={compactNaira(sales.total)} accent="#8B1E1E" />
              <Stat label="Orders" value={sales.count} />
              <Stat label="Avg. Order Value" value={compactNaira(sales.count ? sales.total / sales.count : 0)} accent="#C49A4A" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <ChartCard title="Sales by Order Type" data={sales.by_order_type} />
              <ChartCard title="Sales by Payment Method" data={sales.by_payment} />
            </div>
            <DataTable columns={orderCols} rows={sales.orders} searchKeys={["number", "customer_name", "order_type"]} testIdPrefix="rep-orders" emptyText="No orders in range" />
          </>)}
        </TabsContent>

        <TabsContent value="outlets">
          {league && (<>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat label="Group Sales" value={compactNaira(league.totals.sales)} accent="#8B1E1E" />
              <Stat label="Group Gross Profit" value={compactNaira(league.totals.gross_profit)} accent="#4F7A52" />
              <Stat label="Total Orders" value={league.totals.orders} />
              <Stat label="Outlets" value={league.rows.length} accent="#C49A4A" />
            </div>
            <div className="rounded-xl border shadow-sm overflow-hidden" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="outlets-league">
              <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "#F0E9DD" }}><Trophy size={18} style={{ color: "#C49A4A" }} /><h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Outlet Performance League</h3></div>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
                  <th className="text-left px-5 py-3">#</th><th className="text-left px-4 py-3">Outlet</th>
                  <th className="text-right px-4 py-3">Sales</th><th className="text-right px-4 py-3">Orders</th>
                  <th className="text-right px-4 py-3">Avg Order</th><th className="text-right px-4 py-3">Gross Profit</th>
                  <th className="text-right px-5 py-3">Food Cost %</th></tr></thead>
                <tbody>{league.rows.map((r, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: "#F5EFE5" }} data-testid={`league-row-${i}`}>
                    <td className="px-5 py-3"><span className="inline-flex w-6 h-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: RANK[i] || "#d8cbb8" }}>{i + 1}</span></td>
                    <td className="px-4 py-3 font-medium" style={{ color: "#3B2722" }}>{r.location}</td>
                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "#8B1E1E" }}>{naira(r.sales)}</td>
                    <td className="px-4 py-3 text-right">{r.orders}</td>
                    <td className="px-4 py-3 text-right">{naira(r.avg_order)}</td>
                    <td className="px-4 py-3 text-right font-semibold" style={{ color: r.gross_profit >= 0 ? "#4F7A52" : "#A52222" }}>{naira(r.gross_profit)}</td>
                    <td className="px-5 py-3 text-right"><span className="font-semibold" style={{ color: r.food_cost_pct > 35 ? "#A52222" : r.food_cost_pct > 28 ? "#B7791F" : "#4F7A52" }}>{r.food_cost_pct}%</span></td>
                  </tr>
                ))}
                {league.rows.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No outlet data yet.</td></tr>}
                </tbody>
              </table></div>
            </div>
            <div className="mt-4"><ChartCard title="Sales by Outlet" data={league.rows.map((r) => ({ name: r.location, amount: r.sales }))} /></div>
          </>)}
        </TabsContent>

        <TabsContent value="ledger">
          <div className="flex flex-wrap items-end gap-3 mb-6 rounded-xl border p-4" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}>
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={pmStart} onChange={(e) => setPmStart(e.target.value)} className="h-9 w-40" data-testid="pm-start" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={pmEnd} onChange={(e) => setPmEnd(e.target.value)} className="h-9 w-40" data-testid="pm-end" /></div>
            <div className="space-y-1"><Label className="text-xs">TILL</Label><Input placeholder="e.g. Till 1" value={pmTill} onChange={(e) => setPmTill(e.target.value)} className="h-9 w-40" data-testid="pm-till" /></div>
            <Button onClick={loadLedger} data-testid="pm-apply" className="h-9 text-white" style={{ background: "#8B1E1E" }}>Apply Filter</Button>
            {ledger && ledger.rows && ledger.rows.length > 0 && (
              <Button variant="outline" className="h-9" data-testid="pm-export"
                onClick={() => exportCsv("payment-ledger.csv", ledger.rows.map((r) => ({
                  date: r.date, order: r.order_number, till: r.till,
                  cashier: r.cashier_name, method: r.method, amount: r.amount, shift: r.shift_id,
                })))}><Download size={15} className="mr-1" /> Export CSV</Button>
            )}
          </div>

          {ledger && (<>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="pm-totals">
              <Stat label="Total Tendered" value={compactNaira(ledger.grand_total)} accent="#8B1E1E" />
              <Stat label="Cash" value={compactNaira(ledger.by_method?.cash || 0)} accent="#4F7A52" />
              <Stat label="Card (POS)" value={compactNaira(ledger.by_method?.card || 0)} accent="#C49A4A" />
              <Stat label="Transfer" value={compactNaira(ledger.by_method?.transfer || 0)} accent="#6B1717" />
              <Stat label="Tender Rows" value={ledger.count} />
            </div>

            <div className="rounded-xl border shadow-sm overflow-hidden mb-6" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="pm-by-till">
              <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "#F0E9DD" }}>
                <Landmark size={18} style={{ color: "#C49A4A" }} />
                <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Per-TILL Breakdown</h3>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
                  <th className="text-left px-5 py-3">TILL</th>
                  <th className="text-right px-4 py-3">Cash</th>
                  <th className="text-right px-4 py-3">Card</th>
                  <th className="text-right px-4 py-3">Transfer</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-5 py-3">Tender Rows</th>
                </tr></thead>
                <tbody>{(ledger.by_till || []).map((r, i) => (
                  <tr key={i} className="border-b" data-testid={`pm-till-row-${i}`} style={{ borderColor: "#F5EFE5" }}>
                    <td className="px-5 py-3 font-medium" style={{ color: "#3B2722" }}>{r.till}</td>
                    <td className="px-4 py-3 text-right">{naira(r.cash)}</td>
                    <td className="px-4 py-3 text-right">{naira(r.card)}</td>
                    <td className="px-4 py-3 text-right">{naira(r.transfer)}</td>
                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "#8B1E1E" }}>{naira(r.total)}</td>
                    <td className="px-5 py-3 text-right">{r.count}</td>
                  </tr>
                ))}
                {(!ledger.by_till || ledger.by_till.length === 0) && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No tenders in range.</td></tr>
                )}
                </tbody>
              </table></div>
            </div>

            <div className="rounded-xl border shadow-sm overflow-hidden mb-6" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="pm-reconciliation">
              <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "#F0E9DD" }}>
                <ScrollText size={18} style={{ color: "#C49A4A" }} />
                <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Shift Reconciliation</h3>
                <span className="text-xs" style={{ color: "#a58f7c" }}>ledger vs. counted cash</span>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
                  <th className="text-left px-5 py-3">Cashier · TILL</th>
                  <th className="text-left px-4 py-3">Opened</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Float</th>
                  <th className="text-right px-4 py-3">Ledger Cash</th>
                  <th className="text-right px-4 py-3">Counted</th>
                  <th className="text-right px-5 py-3">Variance</th>
                </tr></thead>
                <tbody>{(ledger.reconciliation || []).map((r, i) => (
                  <tr key={i} className="border-b" data-testid={`pm-recon-row-${i}`} style={{ borderColor: "#F5EFE5" }}>
                    <td className="px-5 py-3 font-medium" style={{ color: "#3B2722" }}>{r.cashier_name || "—"} <span className="text-xs" style={{ color: "#a58f7c" }}>· {r.till || "—"}</span></td>
                    <td className="px-4 py-3">{fmtDateTime(r.opened_at)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-right">{naira(r.opening_float)}</td>
                    <td className="px-4 py-3 text-right">{naira(r.ledger_cash)}</td>
                    <td className="px-4 py-3 text-right">{r.counted_cash == null ? <span style={{ color: "#a58f7c" }}>—</span> : naira(r.counted_cash)}</td>
                    <td className="px-5 py-3 text-right font-semibold" style={{ color: r.variance == null ? "#a58f7c" : Math.abs(r.variance) < 0.01 ? "#4F7A52" : "#A52222" }}>
                      {r.variance == null ? "—" : naira(r.variance)}
                    </td>
                  </tr>
                ))}
                {(!ledger.reconciliation || ledger.reconciliation.length === 0) && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No shifts in range.</td></tr>
                )}
                </tbody>
              </table></div>
            </div>

            <div className="rounded-xl border shadow-sm overflow-hidden" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }} data-testid="pm-cashiers">
              <div className="px-5 py-4 border-b" style={{ borderColor: "#F0E9DD" }}>
                <h3 className="font-brand font-semibold" style={{ color: "#3B2722" }}>Per-Cashier Breakdown</h3>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b text-xs uppercase" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: "#F0E9DD" }}>
                  <th className="text-left px-5 py-3">Cashier</th>
                  <th className="text-right px-4 py-3">Cash</th>
                  <th className="text-right px-4 py-3">Card</th>
                  <th className="text-right px-4 py-3">Transfer</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-5 py-3">Rows</th>
                </tr></thead>
                <tbody>{(ledger.by_cashier || []).map((r, i) => (
                  <tr key={i} className="border-b" data-testid={`pm-cashier-row-${i}`} style={{ borderColor: "#F5EFE5" }}>
                    <td className="px-5 py-3 font-medium" style={{ color: "#3B2722" }}>{r.cashier_name || "—"}</td>
                    <td className="px-4 py-3 text-right">{naira(r.cash)}</td>
                    <td className="px-4 py-3 text-right">{naira(r.card)}</td>
                    <td className="px-4 py-3 text-right">{naira(r.transfer)}</td>
                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "#8B1E1E" }}>{naira(r.total)}</td>
                    <td className="px-5 py-3 text-right">{r.count}</td>
                  </tr>
                ))}
                {(!ledger.by_cashier || ledger.by_cashier.length === 0) && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No cashier tenders in range.</td></tr>
                )}
                </tbody>
              </table></div>
            </div>
          </>)}
        </TabsContent>
      </Tabs>
    </Page>
  );
}

function Stat({ label, value, accent }) {
  return <div className="rounded-xl border p-5 shadow-sm" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}><div className="text-sm" style={{ color: "#765A4A" }}>{label}</div><div className="text-2xl font-brand font-bold mt-1" style={{ color: accent || "#3B2722" }}>{value}</div></div>;
}

function ChartCard({ title, data }) {
  return (
    <div className="rounded-xl border shadow-sm p-6" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}>
      <h3 className="font-brand font-semibold mb-3" style={{ color: "#3B2722" }}>{title}</h3>
      {!data || data.length === 0 ? <p className="text-sm" style={{ color: "#a58f7c" }}>No data.</p> : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F0E9DD" />
            <XAxis type="number" tickFormatter={compactNaira} tick={{ fontSize: 11, fill: "#a58f7c" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#765A4A" }} axisLine={false} tickLine={false} tickFormatter={titleCase} />
            <Tooltip formatter={(v) => naira(v)} contentStyle={{ borderRadius: 12, border: "1px solid #ECE4D8" }} />
            <Bar dataKey="amount" radius={[0, 6, 6, 0]} barSize={20}>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
